// AgreementsInfoViewController.tsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Creators as agreementInfoActions } from '@/domain/useCases/scholarshipsDiscounts/getAgreementInfo/actions';
import { Creators as agreementValidations } from '@/domain/useCases/scholarshipsDiscounts/getAgreementValidations/actions';
import { Creators as requestStatusActions } from '@/domain/useCases/procedures/getStudentProcedureStatus';
import AgreementsInfoView from './AgreementsInfoView';
import routes from '@/presentation/components/Routes/routingMap';
import ErrorMessage from '@/presentation/components/Error';
import { SubContainer } from '../../../certificates-constancies/type1/info/components';
import ContainerProcedure from '@/presentation/modules/procedures/components/ContainerProcedure';
import { BackButton } from '@/presentation/modules/procedures/components';

const PROCEDURE_CODE = 1039;

const DOCUMENTS_BY_BENEFICIARY: Record<string, string[]> = {
  '1': [
    'DNI del colaborador.',
    'Última boleta de pago de haberes del colaborador.'
  ],
  '2': [
    'DNI del colaborador.',
    'DNI del cónyuge (alumno).',
    'Acta de matrimonio o declaración jurada simple de convivencia.',
    'Última boleta de pago de haberes del colaborador.'
  ],
  '3': [
    'DNI del colaborador.',
    'DNI del hijo (alumno).',
    'Partida de nacimiento del hijo (alumno).',
    'Última boleta de pago de haberes del colaborador.'
  ]
};

const AgreementsInfoViewController: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { type } = useParams<{ type: string }>();
  const { isLoading, data, error } = useSelector(
    (state: any) => state.getAgreementInfo
  );
  const { isLoading: isLoadingValidations, data: validations } = useSelector(
    (state: any) => state.getAgreementValidations
  );
  const userDni = useSelector((state: any) => state.profile.data.profile.dni);
  const [otherDni, setOtherDni] = useState('');

  const [selectedBeneficiary, setSelectedBeneficiary] = useState('');
  const [showModalNotAvailable, setShowModalNotAvailable] = useState(false);
  const [showModalExistRequest, setShowModalExistRequest] = useState(false);
  const [showModalApproved, setShowModalApproved] = useState(false);
  // track if DNI validation clicked for non-first beneficiary
  const [hasValidatedOther, setHasValidatedOther] = useState(false);

  const [dniToSend, setDniToSend] = useState('');

  useEffect(() => {
    dispatch(agreementInfoActions.getAgreementInfo(PROCEDURE_CODE));
  }, [dispatch, userDni]);

  const {
    title,
    costLabel,
    daysLabel,
    deadlineLabel,
    beneficiaryTypes,
    discountText
  } = useMemo(() => {
    if (!data) {
      return {
        title: '',
        costLabel: '',
        daysLabel: '',
        deadlineLabel: '',
        beneficiaryTypes: [] as any[],
        discountText: ''
      };
    }
    const formattedEndDate = data.endDate
      ? new Date(data.endDate).toLocaleDateString('es-PE', {
          year: 'numeric',
          month: 'short',
          day: '2-digit'
        })
      : '';
    return {
      title: 'Convenio de Grupo Intercorp',
      costLabel: data.cost ?? '',
      daysLabel: data.attentionDuration ?? '',
      deadlineLabel: formattedEndDate,
      beneficiaryTypes: data.beneficiaries ?? [],
      discountText: data.discount
        ? `${data.discount} en tus cuotas disponibles del ciclo.`
        : ''
    };
  }, [data]);

  const requirementsAuto = useMemo(() => {
    if (!validations) return [];
    // for non-first beneficiary, show only after validating DNI
    if (selectedBeneficiary !== beneficiaryTypes[0]?.id && !hasValidatedOther) {
      return [];
    }
    return validations.map((v: any) => ({ text: v.message, valid: v.isValid }));
  }, [validations, selectedBeneficiary, beneficiaryTypes, hasValidatedOther]);

  const documents = useMemo(() => {
    if (!selectedBeneficiary) return [];
    return DOCUMENTS_BY_BENEFICIARY[selectedBeneficiary] ?? [];
  }, [selectedBeneficiary]);

  useEffect(() => {
    if (!data) return;
    const { requestId, isExpired, hasInProgress, isEnabled } = data;
    if (isExpired === true || isEnabled === false) {
      setShowModalNotAvailable(true);
      return;
    }
    if (requestId) {
      setShowModalExistRequest(true);
      return;
    }
    if (hasInProgress) {
      setShowModalApproved(true);
    }
  }, [data]);

  const handleBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleCloseModalNotAvailable = useCallback(() => {
    setShowModalNotAvailable(false);
    history.push(routes.student.procedures.listScholarshipsDiscounts);
  }, [history]);

  const handleButtonModalNotAvailable = useCallback(() => {
    setShowModalNotAvailable(false);
    history.push(routes.student.procedures.listScholarshipsDiscounts);
  }, []);
  const handleButtonModalExistRequest = useCallback(() => {
    if (data?.requestId) {
      dispatch(
        requestStatusActions.getStudentProcedureStatus(
          PROCEDURE_CODE,
          data.requestId
        )
      );
    }
    history.push(`/tramites/becas-descuentos/intercorp/progreso`, {
      from: 'info',
      hadInProgressRequest: true
    });
  }, [data, dispatch, history]);

  const handleCloseModalExistRequest = useCallback(() => {
    setShowModalExistRequest(false);
    history.push(routes.student.procedures.listScholarshipsDiscounts);
  }, [history]);

  const handleStartRequest = useCallback(() => {
    const beneficiaryObj = data?.beneficiaries?.find(
      (b: any) => b.id === selectedBeneficiary
    );
    const beneficiaryName = beneficiaryObj ? beneficiaryObj.name : '';
    const dniColaborador = userDni;
    const dniDependiente = dniToSend;
    history.push(`/tramites/becas-descuentos/${type}/registro`, {
      selectedBeneficiary,
      beneficiaryName,
      documents,
      requirementsAuto,
      dniColaborador,
      dniDependiente
    });
  }, [
    data,
    documents,
    history,
    requirementsAuto,
    selectedBeneficiary,
    type,
    userDni,
    otherDni,
    beneficiaryTypes
  ]);

  const onBeneficiaryChange = useCallback(
    (beneficiary: string) => {
      setSelectedBeneficiary(beneficiary);
      setHasValidatedOther(false);
      if (beneficiary === beneficiaryTypes[0]?.id) {
        dispatch(
          agreementValidations.getAgreementValidations(PROCEDURE_CODE, userDni)
        );
      } else {
        setOtherDni('');
      }
    },
    [dispatch, beneficiaryTypes, userDni]
  );
  const handleOtherDniChange = useCallback((dni: string) => {
    setOtherDni(dni);
  }, []);
  const handleLoadDni = useCallback(() => {
    setDniToSend(otherDni);
    dispatch(
      agreementValidations.getAgreementValidations(PROCEDURE_CODE, otherDni)
    );
    setHasValidatedOther(true);
  }, [dispatch, selectedBeneficiary, otherDni]);

  if (error) {
    return (
      <ContainerProcedure>
        <SubContainer style={{ marginBottom: 24 }}>
          <BackButton onClick={handleBack} fontSize="14px" />
        </SubContainer>
        <ErrorMessage />
      </ContainerProcedure>
    );
  }

  return (
    <AgreementsInfoView
      handleBack={handleBack}
      title={title}
      costLabel={costLabel}
      daysLabel={daysLabel}
      deadlineLabel={deadlineLabel}
      beneficiaryTypes={beneficiaryTypes}
      selectedBeneficiary={selectedBeneficiary}
      requirementsAuto={requirementsAuto}
      otherRequirements={[
        {
          text: 'Si el colaborador aún no es miembro del Club Intercorp',
          linkText: 'inscribirse en la web',
          linkUrl: 'https://colaboradores-intercorp.com/Account/Login'
        },
        {
          text: 'El colaborador debe tener mínimo 1 mes de permanencia en la institución.',
          linkText: '',
          linkUrl: ''
        }
      ]}
      documents={documents}
      onBeneficiaryChange={onBeneficiaryChange}
      discountText={discountText}
      onStartRequest={handleStartRequest}
      isLoading={isLoading}
      isLoadingValidations={isLoadingValidations}
      showModalNotAvailable={showModalNotAvailable}
      showModalExistRequest={showModalExistRequest}
      showModalApproved={showModalApproved}
      handleCloseModalNotAvailable={handleCloseModalNotAvailable}
      handleButtonModalNotAvailable={handleButtonModalNotAvailable}
      handleCloseModalExistRequest={handleCloseModalExistRequest}
      handleButtonModalExistRequest={handleButtonModalExistRequest}
      handleCloseModalApproved={handleButtonModalExistRequest}
      handleButtonModalApproved={handleButtonModalExistRequest}
      otherDni={otherDni}
      onOtherDniChange={handleOtherDniChange}
      onLoadOtherDni={handleLoadDni}
    />
  );
};

export default AgreementsInfoViewController;
